import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faMedal } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const RANK_COLORS = ['#f59e0b', '#94a3b8', '#cd7c3c'];
const ROW_ACCENTS = ['#16a34a', '#0891b2', '#7c3aed', '#d97706', '#3b82f6', '#f43f5e'];

const RankIcon = ({ rank }) => {
    if (rank === 1) return <FontAwesomeIcon icon={faTrophy} style={{ color: '#f59e0b', fontSize: 15 }} />;
    if (rank <= 3) return <FontAwesomeIcon icon={faMedal} style={{ color: RANK_COLORS[rank - 1], fontSize: 14 }} />;
    return <span style={{ fontSize: 13, fontWeight: 700, color: '#9ca3af' }}>{rank}</span>;
};

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.4px', textAlign: align,
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted, bold }) => (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: bold ? 700 : muted ? 400 : 500,
        textAlign: align,
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');
const fmtKg = (n) => (n != null ? `${Number(n).toLocaleString()} kg` : '—');
const fmtTons = (n) => (n != null ? `${parseFloat(n).toFixed(3)} T` : '—');

const SalesLeaderboardTable = ({ items = [], loading }) => {
    const { t } = useTranslation();
    const maxWeight = Math.max(...items.map((r) => r.net_weight_tons ?? 0), 1);

    return (
        <div style={cardStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('common.analyticsWidgets.salesLeaderboard.title')}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{t('common.analyticsWidgets.salesLeaderboard.subtitle')}</div>
                </div>
                {items.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706', backgroundColor: '#fffbeb', borderRadius: 20, padding: '3px 10px' }}>
                        {items.length} staff
                    </span>
                )}
            </div>

            {loading && <EmptyState message="Loading leaderboard..." />}
            {!loading && !items.length && <EmptyState message="No leaderboard data found." />}

            {!loading && items.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                        <thead>
                            <tr>
                                <Th>{t('common.analyticsColumns.rank')}</Th>
                                <Th>{t('common.analyticsColumns.salesStaff')}</Th>
                                <Th align="right">{t('common.analyticsColumns.orders')}</Th>
                                <Th align="right">{t('common.analyticsColumns.cancelled')}</Th>
                                <Th align="right">{t('common.analyticsColumns.delivered')}</Th>
                                <Th align="right">{t('common.analyticsColumns.weightKg')}</Th>
                                <Th>{t('common.analyticsColumns.weightTons')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => {
                                const rank = i + 1;
                                const accent = ROW_ACCENTS[i % ROW_ACCENTS.length];
                                const weight = row.net_weight_tons ?? 0;
                                const weightPct = (weight / maxWeight) * 100;
                                const isTopThree = rank <= 3;

                                return (
                                    <tr
                                        key={row.sales_staff_id ?? i}
                                        style={{
                                            backgroundColor: isTopThree
                                                ? rank === 1 ? '#fffbeb' : rank === 2 ? '#f8fafc' : '#fff7f0'
                                                : i % 2 === 0 ? '#ffffff' : '#fafafa',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isTopThree
                                            ? rank === 1 ? '#fffbeb' : rank === 2 ? '#f8fafc' : '#fff7f0'
                                            : i % 2 === 0 ? '#ffffff' : '#fafafa')}
                                    >
                                        <Cell>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32 }}>
                                                <RankIcon rank={rank} />
                                            </div>
                                        </Cell>
                                        <Cell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{
                                                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                                                    backgroundColor: `${accent}18`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 12, fontWeight: 800, color: accent,
                                                }}>
                                                    {(row.sales_staff_name ?? '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div style={{ fontWeight: 700, color: '#111827', fontSize: 13 }}>
                                                    {row.sales_staff_name ?? '—'}
                                                </div>
                                            </div>
                                        </Cell>
                                        <Cell align="right" bold>
                                            {fmt(row.total_orders)}
                                        </Cell>
                                        <Cell align="right" muted>
                                            {fmt(row.cancelled)}
                                        </Cell>
                                        <Cell align="right">
                                            {fmt(row.delivered)}
                                        </Cell>
                                        <Cell align="right">
                                            {fmtKg(row.net_weight_kg)}
                                        </Cell>
                                        <Cell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontSize: 13, fontWeight: 700, color: '#0891b2', minWidth: 80 }}>
                                                    {fmtTons(weight)}
                                                </span>
                                                <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: '#e5e7eb', minWidth: 60 }}>
                                                    <div style={{
                                                        height: '100%', borderRadius: 3,
                                                        backgroundColor: '#0891b2',
                                                        width: `${weightPct}%`,
                                                        transition: 'width 0.4s',
                                                    }} />
                                                </div>
                                            </div>
                                        </Cell>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

export default SalesLeaderboardTable;
